import React, { useEffect, useState } from 'react'
import { Paper } from '@mui/material'
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts'
import { DashboardInput } from '../utils/DashboardInput'
import { DashboardPercent } from '../utils/DashboardPercent'
import logo1 from '../../../assets/image/dashboard1.png'
import logo2 from '../../../assets/image/dashboard4.png'
import logo3 from '../../../assets/image/dashboard3.png'
import ProvinceService from '../../provinces/services/Provinces.js'
import Controls from '../../../components/Entry/Controls.js'
import MangroDashboardService from '../service/MangroDashboard.js'
import Loader from '../../../components/Entry/Loading.js'

const LINE_COLORS = ['#0FCB8F', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6', '#EC4899']

const DEFAULT_VALUES = {
    village: '',
    year: new Date().getFullYear()
}

export const MangroDashboard = ({ needLoad, setNeedLoad }) => {
    const [sites, setSites] = useState([])
    const [dashboard, setDashboard] = useState({
        plates_bandes: { total: 0 },
        nombre_especes: 0,
        nombre_clients_mangrove: 0,
        jeunes_plants_par_espece: [],
        taux_survie_par_mois: [],
        jeunes_plants_par_an_et_espece: [],
        polygones: {
            total: 0,
            superficie_cumulative: 0,
            restaures: 0,
            a_restaurer: 0,
            a_restaurer_details: []
        }
    })

    const [planters, setPlanters] = useState({
        total_planteurs: 0,
        par_genre: { Homme: 0, Femme: 0 }
    })

    const [values, setValues] = useState({ ...DEFAULT_VALUES })

    // 1. Charger les sites
    useEffect(() => {
        setNeedLoad(true)
        ProvinceService.getAgroSites()
            .then((res) => {
                if (res?.data) setSites(res.data)
            })
            .finally(() => setNeedLoad(false))
    }, [setNeedLoad])

    // 2. Charger le dashboard
    useEffect(() => {
        setNeedLoad(true)
        MangroDashboardService.getMangroDashboard({
            year: values.year,
            village: values.village || undefined
        })
            .then((data) => setDashboard(data))
            .finally(() => setNeedLoad(false))
    }, [values.year, values.village, setNeedLoad])

    // 3. Charger le nombre de planteurs
    useEffect(() => {
        setNeedLoad(true)
        MangroDashboardService.getMangroPlantersCount({
            year: values.year,
            village: values.village || undefined
        })
            .then((data) =>
                setPlanters(
                    data || {
                        total_planteurs: 0,
                        par_genre: { Homme: 0, Femme: 0 }
                    }
                )
            )
            .finally(() => setNeedLoad(false))
    }, [values.year, values.village, setNeedLoad])

    const handleChange = (e) => {
        const { name, value } = e.target
        setValues((prev) => ({ ...prev, [name]: value }))
    }

    // ── handleReset ───────────────────────────────────────────────
    // Vide le filtre site/village ; seule l'année reste à sa valeur
    // par défaut (année courante).
    const handleReset = () => {
        setValues({ ...DEFAULT_VALUES })
    }

    const getYears = () => {
        const current = new Date().getFullYear()
        return Array.from({ length: 9 }, (_, i) => current - 4 + i)
    }

    const dernierTauxSurvie =
        dashboard.taux_survie_par_mois.length > 0
            ? dashboard.taux_survie_par_mois[dashboard.taux_survie_par_mois.length - 1].taux_survie
            : 0

    const { jeunes_plants_par_an_et_espece, polygones } = dashboard
    const { restaures, a_restaurer, total, superficie_cumulative } = polygones

    const especeKeys = Array.from(
        new Set(jeunes_plants_par_an_et_espece.flatMap((row) => Object.keys(row).filter((k) => k !== 'annee')))
    )

    const totalRestaurationConnu = restaures + a_restaurer
    const pourcentageRestaure =
        totalRestaurationConnu > 0 ? Number(((restaures / totalRestaurationConnu) * 100).toFixed(1)) : 0

    const dataDistributionPolygones = [
        { name: 'Polygones Restaurés', quantite: restaures, color: '#10b981' },
        { name: 'Polygones À restaurer', quantite: a_restaurer, color: '#f59e0b' }
    ]

    return (
        <div className="bg-white p-6 rounded-2xl shadow-[0_4px_20px_rgba(210,210,210,0.4)]">
            {/* Intégration de votre composant Loader personnalisé */}
            <Loader isLoading={needLoad} fullscreen={true} />

            {/* Header & Filtres */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-gray-100">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Suivi Mangrove</h1>
                    <p className="text-sm text-slate-400 mt-1">Vue globale et indicateurs de performance</p>
                </div>

                <div className="flex items-center gap-4 w-full sm:w-auto">
                    <div className="w-full sm:w-64">
                        <Controls.AutoCompleteSelect
                            label="Site / Village"
                            name="village"
                            value={values.village || ''}
                            onChange={handleChange}
                            options={sites}
                            labelKey="name"
                            valueKey="id"
                        />
                    </div>
                    <select
                        className="bg-emerald-500 hover:bg-emerald-600 transition-colors px-4 h-10 rounded-lg border-none text-white font-medium shadow-sm cursor-pointer mt-6"
                        name="year"
                        value={values.year}
                        onChange={handleChange}
                    >
                        {getYears().map((year) => (
                            <option key={year} value={year} className="text-slate-800 bg-white">
                                {year}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={handleReset}
                        className="shrink-0 h-10 px-4 mt-6 rounded-lg border border-emerald-500 text-emerald-600 bg-white hover:bg-emerald-500 hover:text-white transition-colors font-medium shadow-sm"
                    >
                        Réinitialiser
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                <DashboardInput
                    title="Plates bandes"
                    showImg={true}
                    textColor="text-emerald-600"
                    src={logo1}
                    value={dashboard.plates_bandes.total}
                />
                <DashboardInput
                    title="Nombre d'espèces"
                    showImg={true}
                    textColor="text-emerald-600"
                    src={logo2}
                    value={dashboard.nombre_especes}
                />
                <DashboardInput
                    title="Clients Mangrove"
                    showImg={true}
                    textColor="text-emerald-600"
                    src={logo2}
                    value={dashboard.nombre_clients_mangrove}
                />
                <DashboardPercent text="Taux de survie (dernier mois)" value={dernierTauxSurvie} />
            </div>

            {/* Section Planteurs */}
            <div className="mt-10 pt-8 border-t border-gray-100">
                <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">Planteurs</h2>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl flex items-center gap-4">
                        <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg font-bold text-xl">
                            {planters.total_planteurs}
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Total planteurs</p>
                        </div>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl flex items-center gap-4">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-lg font-bold text-xl">
                            {planters.par_genre?.Homme || 0}
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Hommes</p>
                        </div>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl flex items-center gap-4">
                        <div className="p-3 bg-pink-50 text-pink-500 rounded-lg font-bold text-xl">
                            {planters.par_genre?.Femme || 0}
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Femmes</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section Espèces & Plants */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
                <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    <h3 className="font-semibold text-slate-700 mb-4">Jeunes plants par espèce</h3>
                    <div className="flex flex-col gap-3 max-h-[320px] overflow-y-auto pr-1">
                        {dashboard.jeunes_plants_par_espece.length === 0 ? (
                            <p className="text-sm text-slate-400 italic py-4 text-center">Aucune donnée disponible.</p>
                        ) : (
                            dashboard.jeunes_plants_par_espece.map((sp) => (
                                <div
                                    key={sp.species_id}
                                    className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100 shadow-sm"
                                >
                                    <span className="text-sm font-medium text-slate-600">{sp.species_name}</span>
                                    <span className="text-lg font-bold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-lg">
                                        {sp.nombre_jeunes_plants.toLocaleString()}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
                    <h3 className="font-semibold text-slate-700 mb-4">Jeunes plants plantés par espèce et par an</h3>
                    {jeunes_plants_par_an_et_espece.length === 0 ? (
                        <div className="h-[280px] flex items-center justify-center text-sm text-slate-400 italic">
                            Aucune donnée historique pour ce filtre.
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={280}>
                            <LineChart
                                data={jeunes_plants_par_an_et_espece}
                                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="annee" stroke="#94a3b8" fontSize={12} />
                                <YAxis stroke="#94a3b8" fontSize={12} />
                                <Tooltip />
                                <Legend />
                                {especeKeys.map((key, index) => (
                                    <Line
                                        key={key}
                                        type="monotone"
                                        dataKey={key}
                                        stroke={LINE_COLORS[index % LINE_COLORS.length]}
                                        strokeWidth={2.5}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                        connectNulls
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* Section Polygones */}
            <div className="mt-12 pt-8 border-t border-gray-100">
                <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <img src={logo3} className="w-6 h-6 object-contain" alt="" />
                    Suivi des Polygones
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl flex items-center gap-4">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-lg font-bold text-xl">{total}</div>
                        <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                                Total Polygones
                            </p>
                            <p className="text-sm font-semibold text-slate-700">Parcelles enregistrées</p>
                        </div>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl flex items-center gap-4">
                        <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg font-bold text-xl">
                            {superficie_cumulative.toLocaleString()}
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                                Superficie totale
                            </p>
                            <p className="text-sm font-semibold text-slate-700">Mètres carrés (m²)</p>
                        </div>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl flex items-center gap-4">
                        <div className="p-3 bg-amber-50 text-amber-600 rounded-lg font-bold text-xl">
                            {pourcentageRestaure}%
                        </div>
                        <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">Avancement</p>
                            <p className="text-sm font-semibold text-slate-700">Taux de restauration</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                    <div className="flex flex-col gap-4">
                        <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-4 flex justify-between items-center">
                            <span className="text-sm font-medium text-emerald-800">Polygones Restaurés</span>
                            <span className="text-2xl font-bold text-emerald-700">{restaures}</span>
                        </div>
                        <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-4 flex justify-between items-center">
                            <span className="text-sm font-medium text-amber-800">Polygones À restaurer</span>
                            <span className="text-2xl font-bold text-amber-600">{a_restaurer}</span>
                        </div>
                    </div>

                    <div className="lg:col-span-2 bg-slate-50 p-5 rounded-2xl border border-slate-100">
                        <p className="text-sm font-medium text-slate-600 mb-4">Répartition de la restauration</p>
                        <ResponsiveContainer width="100%" height={180}>
                            <BarChart
                                data={dataDistributionPolygones}
                                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} />
                                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} />
                                <Tooltip cursor={{ fill: 'rgba(226, 232, 240, 0.4)' }} />
                                <Bar dataKey="quantite" radius={[6, 6, 0, 0]} barSize={50}>
                                    {dataDistributionPolygones.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    )
}
